// Settings page shell — Settings-02 / Settings-03.
//
// Tab layout depends on the launch permission model (Settings-03):
//
//   User Profile  — everyone
//   Integrations  — everyone
//   Team Members  — owner / admin
//   Locations     — owner / admin
//
// Members and sales (and producers without admin role) only see
// User Profile. The active tab is URL-addressable via ``?tab=…``;
// when a user lands on a tab they aren't allowed to see (deep link
// from before a role change, or hand-typed URL) the page falls back
// to whichever tab is allowed and keeps the URL aligned so a
// refresh stays inside their permissions.

import { useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

import { usePermissions } from "@/features/auth";

import { IntegrationsTab } from "./IntegrationsTab";
import { LLMProviderKeysTab } from "./LLMProviderKeysTab";
import { LocationsTab } from "./LocationsTab";
import { SettingsTeamMembersPage } from "./SettingsTeamMembersPage";
import { UserProfileTab } from "./UserProfileTab";


export type SettingsTab =
  | "user-profile"
  | "integrations"
  | "team-members"
  | "locations"
  | "llm-provider-keys";


interface TabOption {
  value: SettingsTab;
  label: string;
  testId: string;
}


const ALL_TAB_OPTIONS: ReadonlyArray<TabOption> = [
  {
    value: "user-profile",
    label: "User Profile",
    testId: "settings-tab-user-profile",
  },
  {
    value: "integrations",
    label: "Integrations",
    testId: "settings-tab-integrations",
  },
  {
    value: "team-members",
    label: "Team Members",
    testId: "settings-tab-team-members",
  },
  {
    value: "locations",
    label: "Locations",
    testId: "settings-tab-locations",
  },
  {
    value: "llm-provider-keys",
    label: "LLM Provider Keys",
    testId: "settings-tab-llm-provider-keys",
  },
];


function parseTab(raw: string | null): SettingsTab | null {
  if (
    raw === "user-profile"
    || raw === "integrations"
    || raw === "locations"
    || raw === "team-members"
    || raw === "llm-provider-keys"
  ) {
    return raw;
  }
  return null;
}


export function SettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const permissions = usePermissions();

  // Settings-03 — only render tabs the active membership is allowed
  // to use. The order in ``ALL_TAB_OPTIONS`` is preserved so the
  // visible tabs match the design (User Profile first, admin tabs
  // after).
  const visibleTabs = useMemo<TabOption[]>(() => {
    return ALL_TAB_OPTIONS.filter((opt) => {
      if (opt.value === "user-profile") return true;
      if (opt.value === "integrations") return true;
      if (opt.value === "team-members") return permissions.can_manage_team;
      if (opt.value === "locations") return permissions.can_manage_locations;
      if (opt.value === "llm-provider-keys") {
        return permissions.can_manage_broker_settings;
      }
      return false;
    });
  }, [
    permissions.can_manage_team,
    permissions.can_manage_locations,
    permissions.can_manage_broker_settings,
  ]);

  // Default tab: Team Members for admin/owner (preserves the
  // pre-Settings-03 muscle memory), User Profile otherwise. Falls
  // back to whichever tab is visible if neither matches (defensive).
  const defaultTab: SettingsTab = useMemo(() => {
    if (permissions.can_manage_team) return "team-members";
    if (visibleTabs.some((t) => t.value === "user-profile")) {
      return "user-profile";
    }
    return visibleTabs[0]?.value ?? "user-profile";
  }, [permissions.can_manage_team, visibleTabs]);

  const requested = parseTab(searchParams.get("tab"));
  const tab: SettingsTab =
    requested !== null && visibleTabs.some((t) => t.value === requested)
      ? requested
      : defaultTab;

  // If the URL points at a tab the user cannot see, rewrite it so a
  // refresh / share lands cleanly inside their allowed surface.
  useEffect(() => {
    if (
      requested !== null
      && !visibleTabs.some((t) => t.value === requested)
    ) {
      const params = new URLSearchParams(searchParams);
      if (defaultTab === "team-members") {
        params.delete("tab");
      } else {
        params.set("tab", defaultTab);
      }
      setSearchParams(params, { replace: true });
    }
  }, [requested, visibleTabs, defaultTab, searchParams, setSearchParams]);

  const onSelectTab = (next: SettingsTab) => {
    if (next === tab) return;
    const params = new URLSearchParams(searchParams);
    if (next === defaultTab && next === "team-members") {
      // Default tab — drop the param to keep URLs minimal.
      params.delete("tab");
    } else {
      params.set("tab", next);
    }
    setSearchParams(params, { replace: false });
  };

  return (
    <div data-testid="settings-page" className="flex flex-col gap-5">
      <header className="flex flex-col gap-1">
        <h1 className="text-lg font-semibold tracking-tight text-text-primary">
          Settings
        </h1>
        <p className="text-sm text-text-secondary">
          Manage your profile, integrations, team members, and broker office
          locations.
        </p>
      </header>

      <nav
        role="tablist"
        aria-label="Settings sections"
        data-testid="settings-tablist"
        className="flex gap-2 border-b border-border-subtle"
      >
        {visibleTabs.map((opt) => {
          const active = opt.value === tab;
          return (
            <button
              key={opt.value}
              type="button"
              role="tab"
              aria-selected={active}
              data-testid={opt.testId}
              onClick={() => onSelectTab(opt.value)}
              className={[
                "px-3 py-1.5 text-xs font-medium",
                active
                  ? "border-b-2 border-teal text-text-primary"
                  : "text-text-secondary hover:text-text-primary",
              ].join(" ")}
            >
              {opt.label}
            </button>
          );
        })}
      </nav>

      <section
        role="tabpanel"
        aria-label={tab}
        data-testid="settings-tabpanel"
      >
        {tab === "user-profile" && <UserProfileTab />}
        {tab === "integrations" && <IntegrationsTab />}
        {tab === "team-members" && permissions.can_manage_team
          && <SettingsTeamMembersPage />}
        {tab === "locations" && permissions.can_manage_locations
          && <LocationsTab />}
        {tab === "llm-provider-keys"
          && permissions.can_manage_broker_settings
          && <LLMProviderKeysTab />}
      </section>
    </div>
  );
}
